/*
    Copyright (C) 2017 Red Hat, Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

            http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Component, Input } from '@angular/core';

import { ConfigModel } from '../../models/config.model';
import { FieldMappingPair, MappedField } from '../../models/mapping.model';
import { DocumentDefinition } from '../../models/document-definition.model';

@Component({
  selector: 'simple-mapping',
  template: `<div class="mappingFieldContainer" *ngIf="fieldPair">
  <div *ngFor="let mappedField of fieldPair.getMappedFields(isSource)" class="MappingFieldSection" on-mouseover='handleMouseOver($event)'
    [attr.draggable]="true" (dragstart)="startDrag($event, mappedField)" (drop)="endDrag($event, mappedField)"
    (dragenter)="dragEnterLeave($event, true)" (dragleave)="dragEnterLeave($event, false)" (dragover)="allowDrop($event)">

    <mapping-field-detail [fieldPair]="fieldPair" [cfg]="cfg" [isSource]="isSource" [mappedField]="mappedField"></mapping-field-detail>
    <mapping-field-action [fieldPair]="fieldPair" [cfg]="cfg" [isSource]="isSource" [mappedField]="mappedField"></mapping-field-action>
  </div>

  <!-- 'Add Source/ Add Target' button -->
  <div class="linkContainer" *ngIf="isAddButtonVisible()">
    <button type="button" class="btn btn-primary" (click)="addClicked()">{{ getAddButtonLabel() }}</button>
  </div>
</div>`,
})

export class SimpleMappingComponent {
  @Input() cfg: ConfigModel;
  @Input() isSource = false;
  @Input() fieldPair: FieldMappingPair;

  private isDragDropTarget = false;
  private elem = null;
  private start = 0;
  private diff = 0;

  isPartialComponent(): boolean {
    return true;
  }

  handleMouseOver(evt1: MouseEvent): void {
    if (this.elem != null) {
      evt1.stopPropagation();
      evt1.preventDefault();
      let end = 0;

      if (evt1['pageY']) {
        end = evt1['pageY'];
      } else if (evt1['clientY']) {
        end = evt1['clientY'];
      }
      this.diff = end - this.start;
      this.elem.style['top'] = this.diff + 'px';
    }
  }

  startDrag(event: any, draggedMappedField: MappedField): void {
    this.cfg.currentDraggedField = draggedMappedField;
    /* this code does correctly constrain the drag movement to the vertical area of
     * the mapping details section. It couldn't correctly identify the on-drop target
    event = event || window.event;
    event.stopPropagation();
    event.preventDefault();
    this.elem = event.currentTarget;

    if (event['pageY']) {
        this.start = event['pageY'];
    } else if (event['clientY']) {
        this.start = event['clientY'];
    }

    this.elem.style.position = 'relative';
    this.elem.onmouseup = (evt: any) => {
      // See endDrag()
      evt.stopPropagation();
      evt.preventDefault();
    };
    */
  }

  dragEnterLeave(event: any, entering: boolean): void {
    this.isDragDropTarget = entering;
  }

  allowDrop(event: any): void {
    event.preventDefault();
    this.isDragDropTarget = true;
  }

  endDrag(event: any, insertBeforeMappedField: MappedField): void {
    this.isDragDropTarget = false;

    const droppedMappedField: MappedField = this.cfg.currentDraggedField;
    if (droppedMappedField == null) {
      return;
    }

    if (insertBeforeMappedField != null && insertBeforeMappedField.actions[0] != null) {
      this.cfg.mappingService.resequenceMappedField(this.fieldPair, droppedMappedField,
        insertBeforeMappedField.actions[0].argumentValues[0].value);
    }
    this.cfg.currentDraggedField = null;
  }

  isAddButtonVisible(): boolean {
    if (this.isSource && this.fieldPair.transition.isCombineMode()) {
      return true;
    } else if (!this.isSource && this.fieldPair.transition.isSeparateMode()) {
      return true;
    }
    return false;
  }

  getTopFieldTypeLabel(): string {
    return this.isSource ? 'Source' : 'Target';
  }

  getAddButtonLabel(): string {
    return this.isSource ? 'Add Source' : 'Add Target';
  }

  addClicked(): void {
    this.fieldPair.addField(DocumentDefinition.getNoneField(), this.isSource);
    this.cfg.mappingService.updateMappedField(this.fieldPair, this.isSource);
  }

  removePair(): void {
    this.cfg.mappingService.removeMappedPair(this.fieldPair);
  }

  removeMappedField(mappedField: MappedField): void {
    this.fieldPair.removeMappedField(mappedField, this.isSource);
    if (this.fieldPair.getMappedFields(this.isSource).length == 0) {
      this.fieldPair.addField(DocumentDefinition.getNoneField(), this.isSource);
    }
    this.cfg.mappingService.updateMappedField(this.fieldPair, this.isSource);
  }
}
